import { Grid } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import { CheckCircle } from '@material-ui/icons';
import React, { useEffect } from 'react';
import { StyledTableCell, StyledTableRow, styles } from './selectable-table-styles';

export interface IRowData {
  id: string;
  rootCell: string;
  subcellOne: string;
  subcellTwo: string;
}

const SelectableTable: React.FC<IEnhancedTableProps> = (props): JSX.Element => {
  const classes = styles();
  const {
    tableData,
    rowsPerPage,
    currentPage,
    allowMultiSelect,
    emptySelectionList,
    onSelection,
  } = props;
  const [selected, setSelected] = React.useState<string[]>([]);

  useEffect(() => {
    if (emptySelectionList) {
      setSelected([]);
    }
  }, [emptySelectionList]);

  const handleClick = (event: React.MouseEvent<unknown>, name: string): void => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: string[] = [];

    if (allowMultiSelect) {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }
    } else {
      if (selectedIndex === -1) {
        newSelected.push(name);
      }
    }

    setSelected(newSelected);
    onSelection(newSelected);
  };

  const isSelected = (name: string): boolean => selected.indexOf(name) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, tableData.length - currentPage * rowsPerPage);

  return (
    <div className={`${classes.tableRoot} fullHeight`}>
      <Paper className={`${classes.paper} fullHeight`}>
        <TableContainer className='fullHeight'>
          <Table
            className='fullHeight'
            aria-labelledby='tableTitle'
            size={'medium'}
            aria-label='enhanced table'
          >
            <TableBody className='fullHeight'>
              {tableData
                .slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <StyledTableRow
                      hover
                      onClick={event => {
                        handleClick(event, row.id);
                      }}
                      role='checkbox'
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                      data-testid={`table-row-${row.id}`}
                    >
                      <StyledTableCell component='th' id={labelId} scope='row' padding='none'>
                        {row.rootCell}
                      </StyledTableCell>
                      <StyledTableCell align='right'>{row.subcellOne}</StyledTableCell>
                      <StyledTableCell align='right'>{row.subcellTwo}</StyledTableCell>
                      {props.showSelectedAdornment && (
                        <StyledTableCell align='right'>
                          {isItemSelected && (
                            <Grid container direction='column' justifyContent='center'>
                              <CheckCircle />
                            </Grid>
                          )}
                        </StyledTableCell>
                      )}
                    </StyledTableRow>
                  );
                })}
              {Array.from({ length: emptyRows }).map(
                (_, i): JSX.Element => {
                  return (
                    <StyledTableRow key={`empty-row-${i}`}>
                      <StyledTableCell>&nbsp;</StyledTableCell>
                      <StyledTableCell align='right'></StyledTableCell>
                      <StyledTableCell align='right'></StyledTableCell>
                      {props.showSelectedAdornment && (
                        <StyledTableCell align='right'></StyledTableCell>
                      )}
                    </StyledTableRow>
                  );
                },
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

interface IEnhancedTableProps {
  tableData: IRowData[];
  rowsPerPage: number;
  currentPage: number;
  allowMultiSelect?: boolean;
  emptySelectionList?: boolean;
  onSelection: (selectedItems: string[]) => void;
  showSelectedAdornment?: boolean;
}

export default SelectableTable;
